import { useCallback, useLayoutEffect, useRef, useState } from "react";
import classes from "./ConnectorLayer.module.scss";

const SCROLL_OVERFLOW = /(auto|scroll|hidden)/;

/**
 * Returns false when the anchor's center is scrolled outside any of its
 * scrollable/clipping ancestors (walking up to, and including the check
 * against, `rootEl`). Used to cull connectors that point at a box that's
 * scrolled out of view, so the line doesn't sweep across the canvas.
 */
function isAnchorWithinScrollParents(el, rootEl) {
  const r = el.getBoundingClientRect();
  const cx = r.left + r.width / 2;
  const cy = r.top + r.height / 2;
  const margin = 2;

  let node = el.parentElement;
  while (node) {
    const style = window.getComputedStyle(node);
    if (SCROLL_OVERFLOW.test(style.overflowX + style.overflowY)) {
      const nr = node.getBoundingClientRect();
      if (
        cx < nr.left - margin ||
        cx > nr.right + margin ||
        cy < nr.top - margin ||
        cy > nr.bottom + margin
      ) {
        return false;
      }
    }
    if (node === rootEl) break;
    node = node.parentElement;
  }
  return true;
}

export default function ConnectorLayer({
  // Brand colour used across the refactored pages (theme.colors.primary[7]).
  color = "var(--mantine-color-primary-7)",
  strokeWidth = 1.5,
  children,
}) {
  const rootRef = useRef(null);
  const rafRef = useRef(0);
  const animatingRef = useRef(0);
  const loopRef = useRef(0);
  const [paths, setPaths] = useState([]);

  const computePaths = useCallback(() => {
    const root = rootRef.current;
    if (!root) return;
    const rootRect = root.getBoundingClientRect();

    const anchors = root.querySelectorAll("[data-conn-anchor]");
    const byId = new Map();
    anchors.forEach((el) => byId.set(el.getAttribute("data-conn-anchor"), el));

    const next = [];
    anchors.forEach((el) => {
      const fromId = el.getAttribute("data-conn-from");
      if (!fromId) return;
      const fromEl = byId.get(fromId);
      if (!fromEl) return;

      const a = fromEl.getBoundingClientRect();
      const b = el.getBoundingClientRect();
      // Skip anchors that aren't laid out (e.g. inside a display:none branch).
      if (
        (a.width === 0 && a.height === 0) ||
        (b.width === 0 && b.height === 0)
      ) {
        return;
      }

      // Cull connectors whose endpoint is scrolled out of its own scroll
      // container (e.g. an entity scrolled out of the horizontal entity
      // row). Without this, the line would sweep across the whole canvas
      // toward an off-screen box. Recomputed on scroll, so connectors
      // fade in/out as their boxes enter/leave the viewport.
      if (
        !isAnchorWithinScrollParents(fromEl, root) ||
        !isAnchorWithinScrollParents(el, root)
      ) {
        return;
      }

      // Coordinates relative to the (non-scrolling) overlay origin.
      const x1 = a.left + a.width / 2 - rootRect.left;
      const y1 = a.top + a.height / 2 - rootRect.top;
      const x2 = b.left + b.width / 2 - rootRect.left;
      const y2 = b.top + b.height / 2 - rootRect.top;

      // Horizontal-biased cubic that reads like a node-editor connector.
      // dx = HALF the horizontal gap so both control points stay inside
      // [x1, x2] — the curve never bulges into the empty space beside the
      // boxes. (A forced minimum offset made the control points cross over
      // and swing the line outside the box columns.)
      const dx = Math.abs(x2 - x1) / 2;

      // Pull the line's ends back into the gap so the stroke sits just off
      // the box edges instead of painting onto them. The approach at each
      // end is horizontal, so nudging along x keeps the curve shape. Clamp
      // the pull-back to the control-point offset so a small gap can't make
      // the ends cross over (which would hook the line back on itself).
      const endGap = Math.min(6, dx);
      const dir = x2 >= x1 ? 1 : -1;
      const sx = x1 + dir * endGap; // start just outside the source box
      const exX = x2 - dir * endGap; // end just before the target box

      const d = `M ${sx} ${y1} C ${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${exX} ${y2}`;
      next.push({ id: `${fromId}__${el.getAttribute("data-conn-anchor")}`, d });
    });

    setPaths((prev) => {
      // Avoid a state churn when nothing actually moved (keeps the rAF loop
      // cheap during animations).
      if (
        prev.length === next.length &&
        prev.every((p, i) => p.id === next[i].id && p.d === next[i].d)
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  const schedule = useCallback(() => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      computePaths();
    });
  }, [computePaths]);

  const runLoop = useCallback(() => {
    computePaths();
    if (animatingRef.current > 0) {
      loopRef.current = requestAnimationFrame(runLoop);
    } else {
      loopRef.current = 0;
    }
  }, [computePaths]);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    computePaths();

    const ro = new ResizeObserver(schedule);
    ro.observe(root);

    const mo = new MutationObserver(schedule);
    mo.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-conn-anchor", "data-conn-from", "style", "class"],
    });

    const onScroll = () => schedule();
    // Capture phase so inner scrollers (the entity row) are caught too.
    root.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", schedule);

    const onTransitionStart = (e) => {
      if (e.propertyName && e.propertyName !== "height") return;
      animatingRef.current += 1;
      if (!loopRef.current) loopRef.current = requestAnimationFrame(runLoop);
    };
    const onTransitionEnd = (e) => {
      if (e.propertyName && e.propertyName !== "height") return;
      animatingRef.current = Math.max(0, animatingRef.current - 1);
      schedule(); // settle on the final layout
    };
    root.addEventListener("transitionstart", onTransitionStart, true);
    root.addEventListener("transitionend", onTransitionEnd, true);
    root.addEventListener("transitioncancel", onTransitionEnd, true);

    return () => {
      ro.disconnect();
      mo.disconnect();
      root.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", schedule);
      root.removeEventListener("transitionstart", onTransitionStart, true);
      root.removeEventListener("transitionend", onTransitionEnd, true);
      root.removeEventListener("transitioncancel", onTransitionEnd, true);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (loopRef.current) cancelAnimationFrame(loopRef.current);
      animatingRef.current = 0;
    };
  }, [computePaths, schedule, runLoop]);

  return (
    <div ref={rootRef} className={classes.root}>
      {children}
      <svg className={classes.overlay} aria-hidden="true">
        {paths.map((p) => (
          <path
            key={p.id}
            d={p.d}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
          />
        ))}
      </svg>
    </div>
  );
}
